/**
 * @file: useFacadeInspection.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { CameraEvent } from '@euler/app/components/media/camera/CameraView.shared';
import { ImageAnnotationResult } from '@euler/app/flows/media/annotation/components/ImageAnnotationView';
import { ImageAnnotationMetadata } from '@euler/app/flows/media/annotation/functions/AnnotationMetadata';
import { PredefinedAnnotationTags } from '@euler/app/flows/media/annotation/functions/tagPresets';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { SiteInspectionManager } from '@euler/app/flows/task/functions/task-detail/inspection/SiteInspectionManager';
import {
  isMediaStagedPending,
  mediaStagedToMediaInfo,
} from '@euler/app/flows/task/functions/task-detail/util';
import { useAppLoading } from '@euler/components/loading/Provider';
import { applyFacadeItemResult } from '@euler/functions/inventory/applyFacadeItemResult';
import {
  facadeDefaultTagOption,
  facadeSiteTagOptions,
} from '@euler/functions/inventory/facadeSiteTagOptions';
import { isRecordOnlySite } from '@euler/functions/inventory/isRecordOnlySite';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useDefaultPreInspectionAnnotationTag } from '@euler/functions/useDefaultPreInspectionAnnotationTag';
import {
  InspectionSiteInfo,
  kFaultFreeSiteCode,
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
} from '@euler/model';
import { ImageAnnotationScene } from '@euler/model/enum';
import { PreInspection } from '@euler/model/task-detail';
import { delayed, showAlert, withConfirmation } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import compareAsc from 'date-fns/compareAsc';
import { useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { FacadeSitePickerView } from '../components/FacadeSitePickerView';
import { useFacadeInspectionFlow } from '../functions/useFacadeInspectionFlow';
import { FacadeMediaInfo } from './types';
import { useFacadeSiteRequirements } from './useFacadeSiteRequirements';

const kContinuousCaptureSiteCodes = new Set<string>([
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
  kFaultFreeSiteCode,
]);

export const useFacadeInspection = () => {
  const navigation = useAppNavigation();
  const appLoading = useAppLoading();
  const isMobile = useIsMobileLayout();

  const {
    taskManager,
    inventoryQuery: { getSiteByCode, getSiteById },
    detail,
    profile,
  } = useTaskContext();

  const getDefaultTag = useDefaultPreInspectionAnnotationTag();

  const { facadeSites, endFacadeInspection } = useFacadeInspectionFlow();

  const { requiredSites, isMediaCaptureRequired } = useFacadeSiteRequirements();

  const [siteInspections] = useBehaviorSubject(
    taskManager.preInspectionManager.siteInspections$,
  );

  const siteInspectionsRef = useRef(siteInspections);
  siteInspectionsRef.current = siteInspections;

  useEffect(() => () => endFacadeInspection(), [endFacadeInspection]);

  //#region commit site inspection results

  const requestCommit = usePersistFn(async (siteId: number) => {
    const manager =
      taskManager.preInspectionManager.getSiteInspectionManager(siteId);

    if (!manager) {
      console.error(
        'Site inspection manager does not exist, this must be a bug',
      );
      return;
    }

    let hasPendingMediaUpload = false;

    manager.enumerate({
      onMedia: media => {
        if (isMediaStagedPending(media)) {
          hasPendingMediaUpload = true;
        }
        return true;
      },
    });

    if (hasPendingMediaUpload) {
      __DEV__ && console.log('site inspection commit queued');
      return;
    }

    try {
      await manager.commit();
      __DEV__ && console.log('site inspection commit success');
    } catch (err) {
      void showAlert('保存数据失败', (err as any)?.message);
    }
  });

  //#endregion

  // the item result follows the results its photos are tagged with - the most
  // defective one wins.
  const updateItemInspectionResult = (
    manager: SiteInspectionManager<PreInspection>,
    site: InspectionSiteInfo,
    key: string,
  ) => {
    manager.produceItem(key, draft => {
      applyFacadeItemResult(draft, site);
    });
  };

  const takeMedia = useMediaCaptureWithAnnotation({
    scene: ImageAnnotationScene.PreInspection,
  });

  //#region handle facade site press
  const onSitePress = usePersistFn((code: string) => {
    const site = getSiteByCode(code);
    if (!site) return;

    let target = site;
    let siteId = target.id;

    let manager =
      taskManager.preInspectionManager.getSiteInspectionManager(siteId);

    if (!manager) return;

    let inspectedSite = siteInspectionsRef.current.get(siteId);
    if (!inspectedSite?.items.length) return;

    const itemKey = () =>
      siteInspectionsRef.current.get(siteId)?.items[0]._key ?? '__notexisted__';

    const title$ = new BehaviorSubject(site.name);
    const cameraEvent$ = new Subject<CameraEvent>();

    const onSelected = (selected: InspectionSiteInfo) => {
      const m = taskManager.preInspectionManager.getSiteInspectionManager(
        selected.id,
      );
      if (!m) return;

      inspectedSite = siteInspectionsRef.current.get(selected.id);
      if (!inspectedSite?.items.length) return;

      target = selected;
      siteId = selected.id;

      manager = m;
      title$.next(target.name);
      cameraEvent$.next({
        kind: 'media-capture-required-changed',
        mediaCaptureRequired: isMediaCaptureRequired(siteId),
      });
    };

    const uploadVideoFile = async (
      localFileUri: string,
      contentType: string,
      rotate: number | undefined,
    ) => {
      taskManager.uploadController.enqueue({
        localFileUri,
        contentType,
        type: 'video',
        parameters: {
          cover: true,
          blurhash: true,
          videoRotate: rotate,
        },
        context: {
          type: 'item-inspection',
          siteId,
          key: itemKey(),
          siteInspectionManagerId: manager!.id,
        },
        listener: async e => {
          if (e.type === 'finished') {
            updateItemInspectionResult(manager!, target, itemKey());
            await requestCommit(siteId);
          }
        },
      });
    };

    const handleImageAnnotationResults = async (
      results: ImageAnnotationResult[],
    ) => {
      let finished = 0;
      let errored = 0;

      const commitWhenReady = () => {
        __DEV__ &&
          console.log('finished count: ', finished, ', errored: ', errored);
        if (finished + errored === results.length) {
          delayed(() => {
            __DEV__ && console.log('committing all', results.length);
            void requestCommit(siteId);
          }, 100);
        }
      };

      const onFinish = () => {
        finished++;
        commitWhenReady();
      };

      const onError = () => {
        errored++;
        commitWhenReady();
      };

      for (const result of results) {
        taskManager.uploadController.enqueue({
          localFileUri: result.imageUri,
          contentType: 'image/jpeg',
          type: 'image',
          // simulateFailure: true,
          parameters: {
            cover: true,
            blurhash: true,
          },
          context: {
            type: 'item-inspection',
            siteId,
            key: itemKey(),
            siteInspectionManagerId: manager!.id,
            annotationMetadata: result.annotationMetadata,
          },
          // eslint-disable-next-line @typescript-eslint/no-loop-func
          listener: async e => {
            if (e.type === 'finished') {
              updateItemInspectionResult(manager!, target, itemKey());
              onFinish();
            } else if (e.type === 'error' || e.type === 'abort') {
              onError();
            }
          },
        });
      }
    };

    takeMedia({
      tags: () => PredefinedAnnotationTags.preInspection,
      tagOptions: () => facadeSiteTagOptions(target),
      defaultTag: () => {
        const isRecord = isRecordOnlySite(target);
        return {
          name: getDefaultTag(isRecord),
          color: !isRecord ? 'warning' : undefined,
          option: isRecord ? undefined : facadeDefaultTagOption(target),
        };
      },
      caption: () => (
        <FacadeSitePickerView
          defaultSite={site!}
          sites={facadeSites ?? []}
          onSelected={onSelected}
        />
      ),
      watermark: (scale?: number) => (
        <TaskImageWatermark
          licensePlateNo={detail.basicInfo.licensePlateNo}
          title={''}
          title$={title$}
          author={profile.user.name}
          store={profile.store.name}
          type="pre-inspection"
          scale={scale}
        />
      ),
      uploadVideoFile,
      handleImageAnnotationResults,
      // seeded off the tapped site; the camera's own site picker can re-target
      // the capture, which pushes the new site's setting over `event$`.
      mediaCaptureRequired: isMediaCaptureRequired(site.id),
      event$: cameraEvent$,
      continuous: isMobile && kContinuousCaptureSiteCodes.has(code),
    });
  });
  //#endregion

  const medias = useMemo<FacadeMediaInfo[]>(() => {
    return [...siteInspections.values()]
      .filter(x => x.bundle === 'facade')
      .flatMap<FacadeMediaInfo>(siteInspection => {
        return siteInspection.items.flatMap<FacadeMediaInfo>(itemInspection =>
          itemInspection.medias.map(x => ({
            ...mediaStagedToMediaInfo(x),
            siteId: siteInspection.siteId,
            siteCode: siteInspection.siteCode,
            siteName: siteInspection.siteName,
            timestamp: x._timestamp,
          })),
        );
      })
      .sort((x, y) => compareAsc(x.timestamp, y.timestamp));
  }, [siteInspections]);

  //#endregion media controller

  const onPreviewVideo = usePersistFn((media: FacadeMediaInfo) => {
    const siteId = media.siteId;
    const manager =
      taskManager.preInspectionManager.getSiteInspectionManager(siteId);

    if (!manager) {
      navigation.navigate('_mediaPreview', { url: media.url, type: 'video' });
      return;
    }

    const itemKey =
      siteInspectionsRef.current.get(siteId)?.items[0]._key ?? '__notexisted__';

    const stagedMedia = manager.findMedia(itemKey, media.key);
    if (
      stagedMedia &&
      stagedMedia._source.type === 'local' &&
      stagedMedia._source.state.status === 'uploaded'
    ) {
      navigation.navigate('_mediaPreview', {
        type: 'video',
        url: stagedMedia._source.localFileUri,
      });
    } else {
      navigation.navigate('_mediaPreview', {
        type: 'video',
        url: media.url,
      });
    }
  });

  const onDeleteMedia = usePersistFn(async (media: FacadeMediaInfo) => {
    if (
      !(await withConfirmation({
        title: '确认',
        message: '是否确认删除该图片?',
      }))
    ) {
      return;
    }
    const siteInspectionManager =
      taskManager.preInspectionManager.getSiteInspectionManager(media.siteId);
    if (!siteInspectionManager) return;

    const siteInspection = siteInspections.get(media.siteId);
    if (!siteInspection) return;

    const item = siteInspection.items[0];
    if (!item) return;

    siteInspectionManager.removeMedia(item._key, media.key);

    const site = getSiteById(media.siteId);
    if (site) {
      updateItemInspectionResult(siteInspectionManager, site, item._key);
    }

    delayed(async () => {
      await appLoading.showWithAction(async () => {
        await requestCommit(media.siteId);
      });
    }, 0);
  });

  const onEditMedia = usePersistFn((mediaInfo: FacadeMediaInfo) => {
    const { siteId } = mediaInfo;
    const site = getSiteById(siteId);
    if (site == null) {
      console.error(
        `[edit-facade-media] cannot find site with id ${siteId}, this must be bug`,
      );
      return;
    }

    const manager =
      taskManager.preInspectionManager.getSiteInspectionManager(siteId);
    if (!manager) return;

    const itemKey =
      siteInspectionsRef.current.get(siteId)?.items[0]._key ?? '__notexisted__';
    const mediaStaged = manager.findMedia(itemKey, mediaInfo.key);

    if (!mediaStaged) return;

    const originalImageUri = mediaStaged.annotationMetadata?.origin_uri;
    navigation.navigate('_imageAnnotation', {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      imageUri: originalImageUri || mediaInfo.url,
      tags: PredefinedAnnotationTags.preInspection,
      tagOptions: facadeSiteTagOptions(site),
      annotationMetadata: originalImageUri
        ? mediaStaged.annotationMetadata
        : undefined,
      watermark: (scale?: number) => (
        <TaskImageWatermark
          licensePlateNo={detail.basicInfo.licensePlateNo}
          title={site.name}
          author={profile.user.name}
          store={profile.store.name}
          type="pre-inspection"
          scale={scale}
        />
      ),
      onDone: results => {
        const previous = mediaStaged.annotationMetadata;
        const annotationMetadata: ImageAnnotationMetadata | undefined =
          results.annotationMetadata
            ? {
                ...results.annotationMetadata,
                // keep pointing at the non-annotated original; on the first
                // annotation that is the flattened image being edited.
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                origin_uri: previous?.origin_uri || mediaInfo.url,
              }
            : previous
            ? // every tag was removed: keep the metadata so that the original
              // stays reachable, with nothing annotated on it.
              { ...previous, keywords: [], objects: [] }
            : undefined;

        if (
          JSON.stringify(previous ?? null) ===
          JSON.stringify(annotationMetadata ?? null)
        ) {
          // nothing was changed, just close the annotation screen.
          navigation.pop();
          return;
        }
        taskManager.uploadController.enqueue({
          localFileUri: results.imageUri,
          contentType: 'image/jpeg',
          type: 'image',
          parameters: {
            cover: true,
            blurhash: true,
          },
          context: {
            type: 'item-inspection',
            siteId,
            key: itemKey,
            mediaKey: mediaInfo.key,
            siteInspectionManagerId: manager!.id,
            annotationMetadata,
          },
          listener: async e => {
            if (e.type === 'finished') {
              updateItemInspectionResult(manager, site, itemKey);
              await requestCommit(siteId);
            }
          },
        });
        navigation.pop();
      },
    });
  });

  const onRetryMediaUpload = usePersistFn((mediaInfo: FacadeMediaInfo) => {
    if (mediaInfo.source?.type !== 'local') return;

    const { siteId } = mediaInfo;
    const site = getSiteById(siteId);
    if (site == null) {
      console.error(
        `[edit-facade-media] cannot find site with id ${siteId}, this must be bug`,
      );
      return;
    }

    const manager =
      taskManager.preInspectionManager.getSiteInspectionManager(siteId);
    if (!manager) return;

    const itemKey =
      siteInspectionsRef.current.get(siteId)?.items[0]._key ?? '__notexisted__';

    taskManager.uploadController.enqueue({
      localFileUri: mediaInfo.source.localFileUri,
      contentType: mediaInfo.source.contentType,
      type: 'image',
      // simulateFailure: true,
      parameters: mediaInfo.source.uploadParameters,
      context: {
        type: 'item-inspection',
        siteId,
        key: itemKey,
        mediaKey: mediaInfo.key,
        siteInspectionManagerId: manager!.id,
      },
      listener: async e => {
        if (e.type === 'finished') {
          updateItemInspectionResult(manager, site, itemKey);
          await requestCommit(siteId);
        }
      },
    });
  });

  //#endregion

  const frontRecordCount = medias.filter(
    x => x.siteCode === kFrontRecordsSiteCode,
  ).length;
  const rearRecordCount = medias.filter(
    x => x.siteCode === kRearRecordsSiteCode,
  ).length;

  return {
    facadeSites,
    siteInspections,
    requiredSites,
    medias,
    frontRecordCount,
    rearRecordCount,
    onSitePress,
    onPreviewVideo,
    onDeleteMedia,
    onEditMedia,
    onRetryMediaUpload,
  };
};
